// client/src/pages/ClientsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { PhoneInput, removeDialCode } from 'react-international-phone';
import 'react-international-phone/style.css';
import {
  createClient,
  deleteClient,
  getClients,
  updateClient,
  Client as ApiClient,
} from '../lib/clientsApi';
import { fetchVendorOptions, type VendorOption } from '../lib/vendorsApi';
import { fieldErrorsOf } from '../lib/apiErrors';
import VendorSelect, { NO_VENDOR } from '../components/VendorSelect';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import { Modal } from '../components/Modal';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { usePagedRows } from '../hooks/usePagedRows';
import SearchInput from '../components/ui/SearchInput';
import { EmptyState, ErrorState } from '../components/ui/States';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { FIELD as FIELD_CLASS } from '../components/ui/tokens';

const PHONE_INPUT_CSS_VARS = {
  '--react-international-phone-height': '40px',
  '--react-international-phone-border-radius': '6px',
  '--react-international-phone-border-color': '#e2e8f0',
  '--react-international-phone-font-size': '14px',
} as React.CSSProperties;

const CLIENTS_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>Manage your client list for quotations and orders.</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Add new clients with name, email, phone, and address</li>
      <li>Edit or delete existing clients</li>
      <li>Use client data when creating or sending quotations</li>
    </ul>
  </div>
);

type Client = ApiClient & {
  id: string;
  phoneCountryCode: string;
  phoneNumber: string;
  labels: string[];
  preferredVendors: string[];
};

const NAME_RE = /^[A-Za-z][A-Za-z0-9\s.-]*$/;
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_RULES: Record<string, { min: number; max: number }> = {
  '+1868': { min: 7, max: 7 },
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+49': { min: 10, max: 11 },
};

const CLIENTS_PAGE_SIZE = 25;

export const ClientsPage: React.FC = () => {
  const confirm = useConfirm();
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // QA 9 Oct retest item 57: client-side search over the whole list `getClients()`
  // already loads. `/clients` stays unpaginated for exactly this (client.controller.js's
  // own comment); Vendors' `?search=` is server-side because its list is not always
  // fully loaded, which does not apply here.
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1868');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [preferredVendors, setPreferredVendors] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Names for the chips and the table. The same shared list (D86) the picker below reads,
  // so this is one request, not two.
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const vendorNameById = useMemo(
    () => new Map(vendorOptions.map((v) => [v._id, v.name])),
    [vendorOptions]
  );

  // Order of adding IS priority — the backend reads the array in order — so a vendor
  // added again after a removal goes to the end, and one already there stays put.
  const addPreferredVendor = (vendorId: string, name: string) => {
    setPreferredVendors((prev) => (prev.includes(vendorId) ? prev : [...prev, vendorId]));
    // The picker's list can be newer than the one read on mount (a vendor added since):
    // keep the picked name so its chip is not "Unknown vendor".
    setVendorOptions((prev) => (prev.some((v) => v._id === vendorId) ? prev : [...prev, { _id: vendorId, name }]));
  };
  const removePreferredVendor = (vendorId: string) => {
    setPreferredVendors((prev) => prev.filter((id) => id !== vendorId));
  };

  const sortedClients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matched = term
      ? clients.filter(
          (c) => (c.name || '').toLowerCase().includes(term) || (c.email || '').toLowerCase().includes(term)
        )
      : clients;
    // Real order (name, A-Z) rather than raw insertion order — matched case-insensitively
    // so a lower-case entry doesn't sort after every upper-case one.
    return [...matched].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [clients, searchTerm]);

  // The list is read whole, so it pages on screen like every other list (UI-08).
  const paged = usePagedRows(sortedClients, CLIENTS_PAGE_SIZE);

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhoneCountryCode('+1868');
    setPhoneNumber('');
    setAddress('');
    setPreferredVendors([]);
    setErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    resetForm();
  };

  const openModal = () => {
    setEditingClient(null);
    setActionError(null);
    setSuccessMsg(null);
    setIsModalOpen(true);
  };

  const openEdit = (client: Client) => {
    setEditingClient(client);
    setActionError(null);
    setSuccessMsg(null);
    setName(client.name || '');
    setEmail(client.email || '');
    setPhoneCountryCode(client.phoneCountryCode || '+1868');
    setPhoneNumber(client.phoneNumber || '');
    setAddress(client.address || '');
    setPreferredVendors(Array.isArray(client.preferredVendors) ? client.preferredVendors : []);
    setErrors({});
    setIsModalOpen(true);
  };

  const normalizeErrorMessage = (err: any) => {
    return err?.message || 'Request failed';
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const list = await getClients();
      const mapped = (list || []).map((c: any) => ({
        ...c,
        id: c.id || c._id,
        phoneCountryCode: c.phoneCountryCode || '+1868',
        phoneNumber: c.phoneNumber || '',
        labels: Array.isArray(c.labels) ? c.labels : [],
        preferredVendors: Array.isArray(c.preferredVendors)
          ? c.preferredVendors.map((v: any) => String(v))
          : [],
      }));
      setClients(mapped as Client[]);
      setActionError(null);
    } catch (err: any) {
      setActionError(normalizeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    // Best-effort: names only. A list that cannot load must not take the Clients screen
    // down; the picker reads the same list itself and says so when it fails.
    fetchVendorOptions()
      .then(setVendorOptions)
      .catch(() => setVendorOptions([]));
  }, []);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const nameValue = name.trim();
    const emailValue = email.trim();
    const phoneValue = phoneNumber.trim();
    const addressValue = address.trim();

    if (!nameValue) nextErrors.name = 'Client name is required.';
    else if (/^\d+$/.test(nameValue)) nextErrors.name = 'Client name cannot be numeric only.';
    else if (!NAME_RE.test(nameValue)) {
      nextErrors.name = 'Client name can include letters, numbers, spaces, dots, or hyphens only.';
    }

    if (!emailValue) {
      nextErrors.email = 'Email is required.';
    } else if (/\s/.test(emailValue)) {
      nextErrors.email = 'Email cannot contain spaces.';
    } else if (!EMAIL_RE.test(emailValue)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!phoneValue) {
      nextErrors.phoneNumber = 'Contact number is required.';
    } else if (!/^\d+$/.test(phoneValue)) {
      nextErrors.phoneNumber = 'Only numbers allowed.';
    } else {
      const rule = PHONE_RULES[phoneCountryCode];
      if (rule && (phoneValue.length < rule.min || phoneValue.length > rule.max)) {
        nextErrors.phoneNumber = `Invalid number length for ${phoneCountryCode}.`;
      }
    }

    if (!addressValue) nextErrors.address = 'Address is required.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAddClient = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        phoneCountryCode,
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
        preferredVendors,
      };

      if (editingClient) {
        await updateClient(editingClient.id, payload);
        setSuccessMsg('Client updated successfully.');
      } else {
        await createClient(payload);
        setSuccessMsg('Client created successfully.');
      }

      await fetchClients();
      closeModal();
    } catch (err: any) {
      const msg = normalizeErrorMessage(err);
      // D114.7: the server names the field (invariant 6); the message is never searched for one.
      setErrors(fieldErrorsOf(err));
      setActionError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client: Client) => {
    // Delete used to fire straight off the row icon with nothing in between, so a
    // mis-click removed a client and every quotation's link to it. ConfirmProvider
    // is the app-wide confirmation, so this reads like every other destructive action.
    const confirmed = await confirm({
      title: 'Delete this client?',
      summary: `${client.name} will be removed from the client list. This cannot be undone.`,
      tone: 'error',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    try {
      await deleteClient(client.id);
      setSuccessMsg('Client deleted successfully.');
      fetchClients();
    } catch (err: any) {
      setActionError(normalizeErrorMessage(err));
    }
  };

  const handleEdit = (client: Client) => {
    openEdit(client);
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Client List</h1>
            <ModuleInfoButton content={CLIENTS_INFO_CONTENT} ariaLabel="Clients information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by name or email"
              className="w-64"
            />
            <Button
              variant="primary"
              onClick={openModal}
              icon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Add client
            </Button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {successMsg && (
            <div className="px-4 py-2 text-xs text-emerald-700 bg-emerald-50 border-b border-emerald-200">
              {successMsg}
            </div>
          )}
          <div className="overflow-auto">
            {/* Fixed layout: the four text columns share the width equally, so the gaps between columns
                stay the same whatever a row holds. */}
            <table className="data-table table-fixed break-words">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>Client name</th>
                  <th>Email</th>
                  <th className="w-44">Contact number</th>
                  <th>Address</th>
                  <th>Preferred vendors</th>
                  <th className="w-24">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paged.visible.map((client, idx) => (
                  <tr key={client.id}>
                    <td className="tabular-nums text-slate-500">{paged.first + idx}</td>
                    <td className="font-medium text-slate-900">{client.name}</td>
                    <td>{client.email || <span className="text-slate-400">-</span>}</td>
                    <td className="whitespace-nowrap">
                      {/* A client with no phone number on file must not show a bare
                          country code (QA 9 Oct retest item 57): phoneCountryCode
                          defaults to +1868 even when there is no number at all, so
                          the country code is only shown alongside an actual number. */}
                      {client.phoneNumber ? (
                        `${client.phoneCountryCode} ${client.phoneNumber}`
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td>{client.address || <span className="text-slate-400">-</span>}</td>
                    <td>
                      {client.preferredVendors.length === 0 ? (
                        <span className="text-slate-400">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {client.preferredVendors.map((id) => (
                            <span
                              key={id}
                              className="rounded-full bg-primary-50 border border-primary-500/40 px-2 py-0.5 text-xs text-primary-600"
                            >
                              {vendorNameById.get(id) || 'Unknown vendor'}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(client)}
                          aria-label={`Edit ${client.name}`}
                          title="Edit"
                          icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDelete(client)}
                          aria-label={`Delete ${client.name}`}
                          title="Delete"
                          icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                        />
                      </div>
                    </td>
                  </tr>
                ))}

                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center">
                      <LoadingState text="Loading clients..." />
                    </td>
                  </tr>
                )}

                {/* A failed load and an empty list are different answers: showing "no clients"
                    when the request actually errored sent people looking for missing records. */}
                {!loading && actionError && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6">
                      <ErrorState message={actionError} onRetry={fetchClients} />
                    </td>
                  </tr>
                )}

                {!loading && !actionError && sortedClients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6">
                      <EmptyState
                        title={searchTerm.trim() ? 'No clients found' : 'No clients yet'}
                        detail={
                          searchTerm.trim()
                            ? 'No client matches that search. Clear it to see the full list.'
                            : 'Add a client to quote and invoice against it.'
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onPageChange={paged.setPage} noun="clients" />
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        title={editingClient ? 'Edit client' : 'Add new client'}
        onClose={closeModal}
        maxWidthClass="max-w-xl"
      >
        <div className="space-y-4">
          {/* Client name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client name</label>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder="Enter name"
              className={`${FIELD_CLASS} h-10 ${errors.name ? 'border-red-300' : 'border-slate-200'}`}
            />
            {errors.name && <div className="mt-1 text-xs text-red-600">{errors.name}</div>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              placeholder="Enter email"
              className={`${FIELD_CLASS} h-10 ${errors.email ? 'border-red-300' : 'border-slate-200'}`}
            />
            {errors.email && <div className="mt-1 text-xs text-red-600">{errors.email}</div>}
          </div>

          {/* Contact number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contact number</label>
            <PhoneInput
              defaultCountry="tt"
              value={`${phoneCountryCode}${phoneNumber}`}
              onChange={(phone, meta) => {
                const digits = removeDialCode({ phone, dialCode: meta.country.dialCode }).replace(/\D/g, '');
                setPhoneCountryCode(`+${meta.country.dialCode}`);
                setPhoneNumber(digits);
                if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: '' }));
              }}
              style={PHONE_INPUT_CSS_VARS}
              className="w-full"
              inputClassName={`!w-full ${errors.phoneNumber ? '!border-red-300' : ''}`}
              placeholder={
                PHONE_RULES[phoneCountryCode]
                  ? `Enter ${PHONE_RULES[phoneCountryCode].min} digit number`
                  : 'Enter phone number'
              }
            />
            {errors.phoneNumber && <div className="mt-1 text-xs text-red-600">{errors.phoneNumber}</div>}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
            <textarea
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                if (errors.address) setErrors((prev) => ({ ...prev, address: '' }));
              }}
              placeholder="Write here..."
              className={`${FIELD_CLASS} min-h-[140px] py-2 resize-none ${
                errors.address ? 'border-red-300' : 'border-slate-200'
              }`}
            />
            {errors.address && <div className="mt-1 text-xs text-red-600">{errors.address}</div>}
          </div>

          {/* Preferred vendors */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Preferred vendors <span className="font-normal text-slate-500">(optional)</span>
            </label>
            <p className="mb-2 text-xs text-slate-500">
              When a quotation is generated for this client, these suppliers are quoted from
              first where they have a matching product. Items they do not carry are still
              quoted from other vendors.
            </p>

            {preferredVendors.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {preferredVendors.map((id, idx) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-50 border border-primary-500/40 px-2 py-0.5 text-xs text-primary-600"
                  >
                    {/* The number is the priority the backend reads, not decoration. */}
                    <span className="font-semibold">{idx + 1}.</span>
                    {vendorNameById.get(id) || 'Unknown vendor'}
                    <button
                      type="button"
                      onClick={() => removePreferredVendor(id)}
                      className="ml-0.5 text-primary-500 hover:text-primary-600"
                      aria-label={`Remove ${vendorNameById.get(id) || 'vendor'}`}
                    >
                      <X aria-hidden="true" className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <VendorSelect
              label="Add a preferred vendor"
              value={NO_VENDOR}
              allowClear={false}
              onChange={(next) => {
                if (next.vendorId) addPreferredVendor(next.vendorId, next.name);
              }}
            />
            {errors.preferredVendors && (
              <div className="mt-1 text-xs text-red-600">{errors.preferredVendors}</div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={closeModal}>Cancel</Button>
            <Button variant="primary" onClick={handleAddClient} loading={saving}>
              {editingClient ? 'Update client' : 'Add client'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
